import {css} from 'aphrodite';
import React, {useEffect, useMemo} from 'react';
import ReactFlow, {useNodesState, useEdgesState, ReactFlowProvider} from 'reactflow';

import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {TaskSchemaStaticNodeData} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {TaskSchemaNodeRowRenderer} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRowRenderer';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import {SimpleBenchlingNodeHandle} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';

import {
  DEFAULT_FLOWCHART_PROPS,
  READ_ONLY_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES,
  FlowchartStaticEdgeData,
  ProcessFlowchartNodeType,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorLeftSidepanel from './ProcessFlowchartConfigEditorLeftSidepanel';
import {
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES,
} from './processFlowchartConfigEditorRegistry';
import ProcessFlowchartConfigEditorRightSidepanel from './ProcessFlowchartConfigEditorRightSidepanel';

interface ProcessFlowchartConfigEditorContentProps {
  flowchart: {
    nodesStaticData: Array<TaskSchemaStaticNodeData>;
    edgesStaticData: Array<FlowchartStaticEdgeData>;
  };
  locked: boolean;
}

/**
 * Horizontal flowchart config editor content that displays the specified flowchart config version.
 *
 * Converts static nodes and edges to interactive nodes and edges.
 */
export default function ProcessFlowchartConfigEditorContent(
  props: ProcessFlowchartConfigEditorContentProps
): JSX.Element {
  // TODO: Replace the entirety of this flowchart with new horizontal flowchart components
  // from the TransitionModalWithFlowchartEditor
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const interactionProps = props.locked ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;

  // TODO: Convert static nodes to interactive nodes
  const initialNodes = useMemo(
    () =>
      props.flowchart.nodesStaticData.map((node, index) => ({
        id: node.id,
        type: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
        position: {x: 0, y: index * 100},
        data: {
          ...node,
          label: node.nodeName,
          rowRenderer: TaskSchemaNodeRowRenderer,
          nodeHeaderHandleRenderer: SimpleBenchlingNodeHandle,
        },
      })),
    [props.flowchart.nodesStaticData]
  );
  // TODO: Convert static edges to interactive edges
  const initialEdges = useMemo(
    () =>
      props.flowchart.edgesStaticData.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.type,
      })),
    [props.flowchart.edgesStaticData]
  );

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  return (
    <div className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContentWrapper)}>
      <ReactFlowWrapper fullWidth={true}>
        <ReactFlowProvider>
          <div className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContent)}>
            <ProcessFlowchartConfigEditorLeftSidepanel />
            <ReactFlow
              {...DEFAULT_FLOWCHART_PROPS}
              {...interactionProps}
              nodes={nodes}
              nodeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES}
              edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
            >
              <HorizontalControls />
              <DefaultBackground />
            </ReactFlow>
            <ProcessFlowchartConfigEditorRightSidepanel />
          </div>
        </ReactFlowProvider>
      </ReactFlowWrapper>
    </div>
  );
}
